import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { PropsWithChildren, useState } from "react";
import { automationQueries } from "@/api/automations.ts";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button.tsx";
import { EditGeneralConfig } from "@/components/manage-personas/capabilities/EditCapabilitiesConfig.tsx";
import { SetupCapabilitiesConfig } from "./SetupCapabilitiesConfig";

export function CapabilitiesConfigModal({
  personaId,
  children,
}: PropsWithChildren<{ personaId: string }>) {
  const [open, setOpen] = useState(false);
  const {
    data: generalConfig,
    isError,
    refetch,
  } = useSuspenseQuery(automationQueries.generalConfig(personaId));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-3xl max-h-screen overflow-y-auto"
        // Prevent closing on Escape key
        onEscapeKeyDown={(e) => e.preventDefault()}
        // Prevent closing when clicking outside
        onInteractOutside={(e) => e.preventDefault()}
      >
        {isError ? (
          <div className="flex flex-col items-center justify-center h-64 gap-2">
            <p>Error loading configuration.</p>
            <Button variant="ghost" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : generalConfig ? (
          <>
            <DialogHeader>
              <DialogTitle>Capabilities Configuration</DialogTitle>
              <DialogDescription>
                Configure your AI agent's capabilities settings to optimize its
                interactions.
              </DialogDescription>
            </DialogHeader>
            <EditGeneralConfig config={generalConfig} agentId={personaId} />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Welcome to Automations</DialogTitle>
              <DialogDescription>
                Let's set up your basic automation preferences
              </DialogDescription>
            </DialogHeader>
            <SetupCapabilitiesConfig
              personaId={personaId}
              onFinish={() => setOpen(false)}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
